import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TableService } from '../../services/table.service';
import { DataTable, TableColumn, TablePermission } from '../../types/table.types';
import { RootState } from '../index';

// Интерфейс состояния
interface TableState {
  tables: DataTable[];
  currentTable: DataTable | null;
  tablePermissions: TablePermission[];
  isLoading: boolean;
  error: string | null;
}

// Начальное состояние
const initialState: TableState = {
  tables: [],
  currentTable: null,
  tablePermissions: [],
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchTables = createAsyncThunk(
  'tables/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await TableService.getAllTables();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки таблиц');
    }
  }
);

export const fetchTableById = createAsyncThunk(
  'tables/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await TableService.getTableById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки таблицы');
    }
  }
);

export const createTable = createAsyncThunk(
  'tables/create',
  async (tableData: any, { rejectWithValue }) => {
    try {
      const response = await TableService.createTable(tableData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка создания таблицы');
    }
  }
);

export const updateTable = createAsyncThunk(
  'tables/update',
  async ({ id, tableData }: { id: string; tableData: any }, { rejectWithValue }) => {
    try {
      const response = await TableService.updateTable(id, tableData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка обновления таблицы');
    }
  }
);

export const deleteTable = createAsyncThunk(
  'tables/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await TableService.deleteTable(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка удаления таблицы');
    }
  }
);

export const fetchTablePermissions = createAsyncThunk(
  'tables/fetchPermissions',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await TableService.getTablePermissions(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки разрешений таблицы');
    }
  }
);

export const addColumn = createAsyncThunk(
  'tables/addColumn',
  async ({ tableId, columnData }: { tableId: string; columnData: any }, { rejectWithValue }) => {
    try {
      const response = await TableService.addColumn(tableId, columnData);
      return { tableId, column: response };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка добавления колонки');
    }
  }
);

export const updateColumn = createAsyncThunk(
  'tables/updateColumn',
  async ({ tableId, columnId, columnData }: { tableId: string; columnId: string; columnData: any }, { rejectWithValue }) => {
    try {
      const response = await TableService.updateColumn(tableId, columnId, columnData);
      return { tableId, column: response };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка обновления колонки');
    }
  }
);

export const deleteColumn = createAsyncThunk(
  'tables/deleteColumn',
  async ({ tableId, columnId }: { tableId: string; columnId: string }, { rejectWithValue }) => {
    try {
      await TableService.deleteColumn(tableId, columnId);
      return { tableId, columnId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка удаления колонки');
    }
  }
);

// Slice
const tableSlice = createSlice({
  name: 'tables',
  initialState,
  reducers: {
    clearTableError: (state) => {
      state.error = null;
    },
    clearCurrentTable: (state) => {
      state.currentTable = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all tables
      .addCase(fetchTables.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTables.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tables = action.payload;
      })
      .addCase(fetchTables.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch table by ID
      .addCase(fetchTableById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTableById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTable = action.payload;
      })
      .addCase(fetchTableById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create table
      .addCase(createTable.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTable.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tables.push(action.payload);
      })
      .addCase(createTable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update table
      .addCase(updateTable.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTable.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.tables.findIndex(table => table.id === action.payload.id);
        if (index !== -1) {
          state.tables[index] = action.payload;
        }
        if (state.currentTable && state.currentTable.id === action.payload.id) {
          state.currentTable = action.payload;
        }
      })
      .addCase(updateTable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Delete table
      .addCase(deleteTable.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteTable.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tables = state.tables.filter(table => table.id !== action.payload);
        if (state.currentTable && state.currentTable.id === action.payload) {
          state.currentTable = null;
        }
      })
      .addCase(deleteTable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch table permissions
      .addCase(fetchTablePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTablePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tablePermissions = action.payload;
      })
      .addCase(fetchTablePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Add column
      .addCase(addColumn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addColumn.fulfilled, (state, action) => {
        state.isLoading = false;
        // Обновление колонок в currentTable если это та же таблица
        if (state.currentTable && state.currentTable.id === action.payload.tableId) {
          state.currentTable.columns.push(action.payload.column);
        }
      })
      .addCase(addColumn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update column
      .addCase(updateColumn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateColumn.fulfilled, (state, action) => {
        state.isLoading = false;
        // Обновление колонки в currentTable если это та же таблица
        if (state.currentTable && state.currentTable.id === action.payload.tableId) {
          const index = state.currentTable.columns.findIndex(column => column.id === action.payload.column.id);
          if (index !== -1) {
            state.currentTable.columns[index] = action.payload.column;
          }
        }
      })
      .addCase(updateColumn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Delete column
      .addCase(deleteColumn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteColumn.fulfilled, (state, action) => {
        state.isLoading = false;
        // Удаление колонки из currentTable если это та же таблица
        if (state.currentTable && state.currentTable.id === action.payload.tableId) {
          state.currentTable.columns = state.currentTable.columns.filter(column => column.id !== action.payload.columnId);
        }
      })
      .addCase(deleteColumn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearTableError, clearCurrentTable } = tableSlice.actions;

export const selectTables = (state: RootState) => state.tables.tables;
export const selectCurrentTable = (state: RootState) => state.tables.currentTable;
export const selectTablePermissions = (state: RootState) => state.tables.tablePermissions;
export const selectTableLoading = (state: RootState) => state.tables.isLoading;
export const selectTableError = (state: RootState) => state.tables.error;

export default tableSlice.reducer;